import { useMemo, useState } from 'react';
import {
  ExampleCallout,
  FieldLabel,
  InfoSection,
  PanelTitle,
  ResultsEmpty,
  ToolPageHeading,
  ToolTwoColumn,
  fmtUsd,
  parseNum,
  toolInputClass,
} from './toolShared';

const DEFAULTS = { units: '', bump: '', occ: '95', cap: '7' };

export function ToolValueAdd() {
  const [v, setV] = useState(DEFAULTS);

  const result = useMemo(() => {
    const units = parseNum(v.units);
    const bump = parseNum(v.bump);
    const occ = Math.min(100, Math.max(0, parseNum(v.occ))) / 100;
    const cap = parseNum(v.cap) / 100;
    if (units <= 0 || cap <= 0) return null;
    const annualLift = units * bump * 12 * occ;
    const valueAdd = annualLift / cap;
    return { annualLift, valueAdd };
  }, [v]);

  return (
    <div className="space-y-8">
      <ToolPageHeading
        title="Value-add calculator"
        subtitle="Estimate property value increase from in-place rent improvements."
      />

      <InfoSection title="How to use this calculator">
        <p>
          We apply occupancy to monthly rent lift, annualize, then divide by cap rate (
          <span className="font-mono text-[var(--home-miami-blue)]">value ≈ annual NOI lift ÷ cap rate</span>).
        </p>
        <ul className="list-disc space-y-1 pl-5">
          <li>
            <strong className="text-[var(--home-text-on-dark)]">Number of units</strong> — Count of units receiving the rent
            increase.
          </li>
          <li>
            <strong className="text-[var(--home-text-on-dark)]">Rent increase per month</strong> — Average monthly increase
            per unit ($).
          </li>
          <li>
            <strong className="text-[var(--home-text-on-dark)]">Cap rate</strong> — Stabilized or exit cap for valuing the
            income stream.
          </li>
        </ul>
        <ExampleCallout>
          20 units × $150/mo × 12 × 95% occupancy = $342,000 annual lift. At a 7% cap, that implies ~$4.89M of value
          creation (illustrative).
        </ExampleCallout>
      </InfoSection>

      <ToolTwoColumn
        onReset={() => setV(DEFAULTS)}
        left={
          <>
            <PanelTitle>Property details</PanelTitle>
            <div className="mb-6 space-y-4">
              <div className="space-y-1.5">
                <FieldLabel htmlFor="va-u" tip="Units benefiting from the rent increase.">
                  Number of units
                </FieldLabel>
                <input
                  id="va-u"
                  type="text"
                  inputMode="decimal"
                  value={v.units}
                  onChange={(e) => setV((s) => ({ ...s, units: e.target.value }))}
                  className={toolInputClass}
                  placeholder="0"
                />
              </div>
              <div className="space-y-1.5">
                <FieldLabel htmlFor="va-b" tip="Average monthly rent increase per unit ($).">
                  Rent increase per month
                </FieldLabel>
                <div className="flex rounded-xl border border-white/12 bg-white/[0.08] shadow-inner shadow-black/25 backdrop-blur-sm focus-within:border-[var(--home-miami-blue)] focus-within:ring-2 focus-within:ring-[var(--home-miami-blue)]/30">
                  <span className="flex items-center pl-3 font-body text-sm text-[var(--home-muted-on-dark)]">$</span>
                  <input
                    id="va-b"
                    type="text"
                    inputMode="decimal"
                    value={v.bump}
                    onChange={(e) => setV((s) => ({ ...s, bump: e.target.value }))}
                    className={`${toolInputClass} border-0 bg-transparent pl-1 shadow-none ring-0 focus:ring-0`}
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>
            <PanelTitle>Market assumptions</PanelTitle>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <FieldLabel htmlFor="va-o" tip="Economic occupancy during stabilization (0–100%).">
                  Occupancy rate
                </FieldLabel>
                <div className="flex items-center rounded-xl border border-white/12 bg-white/[0.08] shadow-inner shadow-black/25 backdrop-blur-sm focus-within:border-[var(--home-miami-blue)] focus-within:ring-2 focus-within:ring-[var(--home-miami-blue)]/30">
                  <input
                    id="va-o"
                    type="text"
                    inputMode="decimal"
                    value={v.occ}
                    onChange={(e) => setV((s) => ({ ...s, occ: e.target.value }))}
                    className={`${toolInputClass} border-0 bg-transparent shadow-none ring-0 focus:ring-0`}
                    placeholder="95.00"
                  />
                  <span className="pr-3 font-body text-sm text-[var(--home-muted-on-dark)]">%</span>
                </div>
              </div>
              <div className="space-y-1.5">
                <FieldLabel htmlFor="va-c" tip="Cap rate for capitalizing the rent lift (%).">
                  Cap rate
                </FieldLabel>
                <div className="flex items-center rounded-xl border border-white/12 bg-white/[0.08] shadow-inner shadow-black/25 backdrop-blur-sm focus-within:border-[var(--home-miami-blue)] focus-within:ring-2 focus-within:ring-[var(--home-miami-blue)]/30">
                  <input
                    id="va-c"
                    type="text"
                    inputMode="decimal"
                    value={v.cap}
                    onChange={(e) => setV((s) => ({ ...s, cap: e.target.value }))}
                    className={`${toolInputClass} border-0 bg-transparent shadow-none ring-0 focus:ring-0`}
                    placeholder="7.00"
                  />
                  <span className="pr-3 font-body text-sm text-[var(--home-muted-on-dark)]">%</span>
                </div>
              </div>
            </div>
          </>
        }
        right={
          <>
            <PanelTitle>Value-add results</PanelTitle>
            {result === null ? (
              <ResultsEmpty message="Enter property details to calculate value increase." />
            ) : (
              <div className="flex min-h-[220px] flex-col justify-center gap-6">
                <div>
                  <p className="font-body text-xs uppercase tracking-wide text-[var(--home-muted-on-dark)]">
                    Annual rent lift (NOI)
                  </p>
                  <p className="font-display text-2xl font-bold text-[var(--home-text-on-dark)]">
                    {fmtUsd(result.annualLift)}
                  </p>
                </div>
                <div>
                  <p className="font-body text-xs uppercase tracking-wide text-[var(--home-muted-on-dark)]">
                    Implied value increase
                  </p>
                  <p className="font-display text-2xl font-bold text-emerald-300">{fmtUsd(result.valueAdd)}</p>
                </div>
              </div>
            )}
          </>
        }
      />
    </div>
  );
}
